import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Collapse,
  Link,
  Stack,
  Typography,
  useTheme,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import pluralize from "pluralize";
import { compact } from "lodash-es";
import { CommandButton } from "components/CommandButton";
import { DataGrid } from "components/DataGrid";
import { FacilityUsageQueryQuery } from "generated-graphql/graphql";
import { usePaginationModel } from "hooks/usePaginationModel";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import invariant from "tiny-invariant";
import { useFacilityUsage } from "./useFacilityUsage";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";

type Always<T> = T extends null | undefined ? never : T;

type FacilityUsageItem = Always<
  FacilityUsageQueryQuery["facilityUsage"]["items"]
>[0];

type FacilityUsageProps = {
  chemicalId: string;
};

const getCounts = (data: FacilityUsageQueryQuery | undefined) => ({
  tenantCount: data?.facilityUsage.tenantCount ?? 0,
  facilityCount: data?.facilityUsage.facilityCount ?? 0,
  thisYearFacilityCount: data?.facilityUsage.thisYearFacilityCount ?? 0,
});

export const FacilityUsage: React.FC<FacilityUsageProps> = ({ chemicalId }) => {
  // Stores whether we should show the datagrid of facilities or not
  const [showDataGrid, setShowDataGrid] = useState(false);
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const { loading, data, previousData } = useFacilityUsage({
    // only executes the query if the chemicalId is truthy
    chemicalId: chemicalId,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    includeItems: showDataGrid,
  });

  const memoizedCounts = useMemo(() => {
    if (loading) return getCounts(previousData);
    return getCounts(data);
  }, [data, loading, previousData]);

  const tenantCount = memoizedCounts.tenantCount;
  const facilityCount = memoizedCounts.facilityCount;
  const thisYearFacilityCount = memoizedCounts.thisYearFacilityCount;
  const columns: GridColDef<FacilityUsageItem>[] = [
    {
      field: "name",
      headerName: "Facility",
      sortable: false,
      flex: 0.75,
      renderCell: ({ id, row }) => {
        return (
          <Link
            href={`/o/${row.tenant?.id}/chemicals/inventory/${id}/chemicals`}
            style={{ textDecoration: "none" }}
            target="_blank"
          >
            <Box display="flex" alignItems="center">
              {row.name}{" "}
              <OpenInNew
                style={{
                  fontSize: "inherit",
                  marginLeft: theme.spacing(0.5),
                }}
              />
            </Box>
          </Link>
        );
      },
    },
    {
      field: "state",
      headerName: "State",
      sortable: false,
      maxWidth: 50,
    },
    {
      field: "streetAddress1",
      headerName: "Address",
      sortable: false,
      flex: 0.5,
      minWidth: 200,
      valueGetter: (_, row) => {
        const { streetAddress1, streetAddress2, city, state, zip, country } =
          row;

        return compact([
          streetAddress1,
          streetAddress2,
          city,
          state,
          zip,
          country,
        ]).join(", ");
      },
    },
    {
      field: "reportingYears",
      renderHeader: (params) => {
        return (
          <Tooltip
            title="Shows you years in which this chemical is present"
            placement="top"
          >
            <span>Inventory</span>
          </Tooltip>
        );
      },
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }) => {
        const years = Array.isArray(row.reportingYears)
          ? [...row.reportingYears]
          : [];
        // Sort years descending (most recent first)
        years.sort((a, b) => b - a);

        const formattedYears = years
          .map(
            (year) =>
              `${year}${year === currentTierIIReportingYear ? " 🔹" : ""}`
          )
          .join(", ");

        return <Typography variant="body2">{formattedYears}</Typography>;
      },
    },
    ...(tenantCount > 1
      ? [
          {
            field: "tenantName",
            headerName: "Organization",
            flex: 0.25,
            valueGetter: (_, row) => row?.tenant?.name,
          } as GridColDef<FacilityUsageItem>,
        ]
      : []),
  ];
  const { tenantId } = useParams();
  invariant(tenantId);

  const theme = useTheme();
  const facilities = data?.facilityUsage.items ?? [];

  const handleCollapse = useCallback(() => {
    setShowDataGrid((show) => !show);
  }, [setShowDataGrid]);

  if (facilityCount === 0) return <></>;

  return (
    <Stack>
      <Stack direction="row" alignItems="center">
        <Typography>
          {`Changing this information will affect what is being reported at ${pluralize(
            "facility",
            thisYearFacilityCount,
            true
          )}, and previous years' inventory for ${pluralize(
            "facility",
            facilityCount,
            true
          )}`}
        </Typography>
        <CommandButton
          onClick={handleCollapse}
          sx={{ marginLeft: theme.spacing(2) }}
        >
          {showDataGrid ? "Hide Facilities" : "Show Facilities"}
        </CommandButton>
      </Stack>
      <Box
        paddingTop={theme.spacing(2)}
        paddingBottom={showDataGrid ? theme.spacing(4) : undefined}
      >
        <Collapse in={showDataGrid}>
          <DataGrid
            sx={{
              minHeight: "250px",
              opacity:
                loading && previousData
                  ? theme.palette.action.disabledOpacity
                  : 1,
            }}
            isCellEditable={() => false}
            isRowSelectable={() => false}
            loading={loading}
            rows={facilities}
            columns={columns}
            rowCount={facilityCount ?? 0}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
          />
        </Collapse>
      </Box>
    </Stack>
  );
};
